import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  FaStar,
  FaBullhorn,
  FaFire,
  FaRocket,
  FaCoins,
  FaCrown,
  FaArrowRight,
  FaShieldAlt
} from 'react-icons/fa';
import '../styles/FeaturedITOs.css';

// Helper to check if an ITO is truly active
const isItoTrulyActive = (ito) => {
  if (!ito?.isActive) return false;
  if (ito.maxAmount > 0 && ito.mintedAmount >= ito.maxAmount) return false;
  if (ito.endTime > 0 && ito.endTime < Math.floor(Date.now() / 1000)) return false;
  return true;
};

// Get verified creators list
const getVerifiedCreators = () => {
  const saved = localStorage.getItem('rarecanvas_verified_creators');
  return saved ? JSON.parse(saved) : [];
};

// Get featured collections list
const getFeaturedCollections = () => {
  const saved = localStorage.getItem('rarecanvas_featured_collections');
  return saved ? JSON.parse(saved) : [];
};

const FeaturedITOs = () => {
  const navigate = useNavigate();
  const [featuredITOs, setFeaturedITOs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [verifiedCreators, setVerifiedCreators] = useState([]);

  useEffect(() => {
    loadFeaturedITOs();
  }, []);

  const loadFeaturedITOs = async () => {
    setIsLoading(true);

    try {
      // Get featured collections from localStorage
      const featuredList = getFeaturedCollections();
      const verified = getVerifiedCreators();
      setVerifiedCreators(verified);

      // Filter out expired ones
      const activeFeatured = featuredList.filter(f => Date.now() < f.expiresAt);

      if (activeFeatured.length === 0) {
        setFeaturedITOs([]);
        setIsLoading(false);
        return;
      }

      // Fetch ITO data for each featured collection
      const itoPromises = activeFeatured.map(async (featured) => {
        try {
          // Get ITO info
          const itoResponse = await axios.get(
            `https://api.mainnet.klever.org/v1.0/ito/${featured.assetId}`
          );
          const ito = itoResponse.data.data?.ito;

          // Get asset/collection info
          const assetResponse = await axios.get(
            `https://api.mainnet.klever.org/v1.0/assets/${featured.assetId}`
          );
          const asset = assetResponse.data.data?.asset;

          if (ito && isItoTrulyActive(ito)) {
            return {
              ...ito,
              asset,
              featured,
              isBoost: featured.isBoost || featured.service === 'launch_boost',
              isVerified: verified.some(v => v.wallet?.toLowerCase() === ito.receiverAddress?.toLowerCase() || v.wallet?.toLowerCase() === asset?.ownerAddress?.toLowerCase())
            };
          }
          return null;
        } catch (error) {
          console.log(`ITO not found for ${featured.assetId}`);
          return null;
        }
      });

      const results = await Promise.all(itoPromises);
      const validITOs = results.filter(Boolean);

      // Sort by boost status (boosts first), then by added date
      validITOs.sort((a, b) => {
        if (a.isBoost && !b.isBoost) return -1;
        if (!a.isBoost && b.isBoost) return 1;
        return b.featured.addedAt - a.featured.addedAt;
      });

      setFeaturedITOs(validITOs);
    } catch (error) {
      console.error('Error loading featured ITOs:', error);
      setFeaturedITOs([]);
    }

    setIsLoading(false);
  };

  // Check if creator is verified
  const isCreatorVerified = (wallet) => {
    return verifiedCreators.some(v => v.wallet?.toLowerCase() === wallet?.toLowerCase());
  };

  if (isLoading) {
    return null; // Don't show loading state, just hide section
  }

  if (featuredITOs.length === 0) {
    return null; // Don't show if no featured ITOs
  }

  return (
    <div className="featured-itos-section">
      <div className="featured-header">
        <div className="featured-title">
          <FaCrown className="crown-icon" />
          <h2>Featured Collections</h2>
          <span className="featured-badge">Promoted</span>
        </div>
        <p className="featured-subtitle">Premium collections handpicked for you</p>
      </div>

      <div className="featured-grid">
        {featuredITOs.map((ito) => {
          const asset = ito.asset || {};
          const progress = ito.maxAmount > 0 ? (ito.mintedAmount / ito.maxAmount) * 100 : 0;
          const priceData = ito.packData?.[0]?.packs?.[0];
          const price = priceData ? priceData.price / Math.pow(10, 6) : 0;
          const currency = ito.packData?.[0]?.key || 'KLV';

          return (
            <div
              key={ito.assetId}
              className={`featured-card ${ito.isBoost ? 'boost' : ''}`}
              onClick={() => navigate(`/collections/${ito.assetId}`)}
            >
              {/* Badges */}
              <div className="featured-badges">
                {ito.isBoost ? (
                  <span className="badge boost">
                    <FaBullhorn /> Launch Boost
                  </span>
                ) : (
                  <span className="badge featured">
                    <FaStar /> Featured
                  </span>
                )}
                {ito.isVerified && (
                  <span className="badge verified">
                    <FaShieldAlt /> Verified
                  </span>
                )}
              </div>

              {/* Image */}
              <div className="featured-card-image">
                {asset.logo ? (
                  <img
                    src={asset.logo.replace('ipfs://', 'https://ipfs.io/ipfs/')}
                    alt={asset.name}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div className="image-placeholder" style={{ display: asset.logo ? 'none' : 'flex' }}>
                  <FaRocket />
                </div>
                <div className="live-badge">
                  <FaFire /> Live
                </div>
              </div>

              {/* Content */}
              <div className="featured-card-content">
                <h3 className="collection-name">{asset.name || ito.assetId}</h3>
                <p className="collection-ticker">{ito.assetId}</p>

                {/* Progress */}
                <div className="mint-progress">
                  <div className="progress-info">
                    <span>Progress</span>
                    <span>{ito.mintedAmount?.toLocaleString()} / {ito.maxAmount?.toLocaleString()}</span>
                  </div>
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{ width: `${Math.min(progress, 100)}%` }}
                    />
                  </div>
                </div>

                {/* Price */}
                <div className="price-row">
                  <div className="price">
                    <FaCoins />
                    <span className="amount">{price.toLocaleString()}</span>
                    <span className="currency">{currency}</span>
                  </div>
                  <button className="mint-btn">
                    Mint Now <FaArrowRight />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FeaturedITOs;
